import React from "react";
import { Box, Flex, Badge } from "theme-ui";
import P from "components/typography/P";
import BasicButton from "components/forms/BasicButton";
import { SubmitIcon } from "components/svg";
import { themevals } from "theme/themevals";
import moment from "moment";

interface QuoteRowProps {
  id: string;
  quoteNumber: string;
  projectName: string;
  amount: number;
  sentDate: string;
  daysPending: number;
  urgency: "critical" | "warning" | "normal";
  status?: number;
  priceLockDepositStatus?: string | null;
  priceLockDepositAmount?: number | null;
  onAccept: (quoteId: string) => void;
  onReview: (quoteId: string) => void;
  onLockPrice?: (quoteId: string) => void;
  onPayNow?: (quoteId: string) => void;
  onConfirmProject?: (quoteId: string) => void;
}

const QuoteRow: React.FC<QuoteRowProps> = ({
  id,
  quoteNumber,
  projectName,
  amount,
  sentDate,
  daysPending,
  urgency,
  status = 0,
  priceLockDepositStatus,
  priceLockDepositAmount,
  onAccept,
  onReview,
  onLockPrice,
  onPayNow,
  onConfirmProject,
}) => {
  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatDate = (date: string): string => {
    return moment(date).format("MMM Do, YYYY");
  };

  // Status 6 states
  const isPriceLocked = status === 6;
  const isDepositPending = isPriceLocked && priceLockDepositStatus === "pending";
  const isDepositReceived = isPriceLocked && priceLockDepositStatus === "received";

  // Badge display
  let badgeText = daysPending + " day" + (daysPending === 1 ? "" : "s") + " pending";
  let badgeVariant = "success";
  if (urgency === "critical") badgeVariant = "danger";
  else if (urgency === "warning") badgeVariant = "warning";

  if (isDepositPending) {
    badgeText = "Deposit Pending";
    badgeVariant = "warning";
  } else if (isDepositReceived) {
    badgeText = "Ready to Confirm";
    badgeVariant = "success";
  }

  const handleQuoteClick = (e: React.MouseEvent) => {
    e.preventDefault();
    window.open("/quotes/" + id, "_blank");
  };

  // Render action buttons based on status + deposit state
  const renderActions = () => {
    if (isDepositReceived && onConfirmProject) {
      // Status 6 + deposit received: Confirm & Start Project
      return (
        <Flex sx={{ gap: "12px" }}>
          <BasicButton
            scheme="primary"
            onClick={() => onConfirmProject(id)}
            styles={{ padding: "8px 16px", fontSize: "13px" }}
          >
            <SubmitIcon fill="#fff" direction="up" />
            Confirm & Start
          </BasicButton>
          <BasicButton
            scheme="secondary"
            outline={true}
            onClick={() => onReview(id)}
            styles={{ padding: "8px 16px", fontSize: "13px" }}
          >
            Review
          </BasicButton>
        </Flex>
      );
    }

    if (isDepositPending && onPayNow) {
      // Status 6 + deposit pending: Pay Now.
      //
      // Session 78: this was a hand-rolled button filled #38B2AC — a raw
      // hex that is not in themevals OR in the maintenance tokens, so it
      // was a third button style appearing nowhere else in the app. Pay
      // Now is simply THE action on this row, so it is the ordinary
      // primary, exactly like Accept and Confirm & Start beside it. A
      // different colour implied a different KIND of action rather than
      // a different label.
      return (
        <Flex sx={{ gap: "12px" }}>
          <BasicButton
            scheme="primary"
            onClick={() => onPayNow(id)}
            styles={{ padding: "8px 16px", fontSize: "13px" }}
          >
            Pay Now
          </BasicButton>
          <BasicButton
            scheme="secondary"
            outline={true}
            onClick={() => onReview(id)}
            styles={{ padding: "8px 16px", fontSize: "13px" }}
          >
            Review
          </BasicButton>
        </Flex>
      );
    }

    // Status 0: Accept + Lock Price + Review
    return (
      <Flex sx={{ gap: "12px" }}>
        <BasicButton
          scheme="primary"
          onClick={() => onAccept(id)}
          styles={{ padding: "8px 16px", fontSize: "13px" }}
        >
          <SubmitIcon fill="#fff" direction="up" />
          Accept
        </BasicButton>
        {onLockPrice && (
          /* Secondary, not a filled colour: locking the price is an
             ALTERNATIVE to accepting, not a higher-ranked action. It was
             filled `themevals.colors.info`, which put a third weight on a
             row that already has a primary. */
          <BasicButton
            scheme="secondary"
            outline={true}
            onClick={() => onLockPrice(id)}
            styles={{ padding: "8px 16px", fontSize: "13px" }}
          >
            Lock Price
          </BasicButton>
        )}
        <BasicButton
          scheme="secondary"
          outline={true}
          onClick={() => onReview(id)}
          styles={{ padding: "8px 16px", fontSize: "13px" }}
        >
          Review
        </BasicButton>
      </Flex>
    );
  };

  return (
    <Box
      sx={{
        backgroundColor: "white",
        border: "1px solid",
        borderColor: themevals.colors.inputOutline,
        borderRadius: "6px",
        padding: "16px",
        marginBottom: "12px",
        "&:last-child": {
          marginBottom: 0,
        },
      }}
    >
      <Flex sx={{ justifyContent: "space-between", alignItems: "flex-start" }}>
        {/* Left Section: Quote Info */}
        <Box sx={{ flex: 1 }}>
          {/* Top Row: Quote Number and Badge */}
          <Flex sx={{ alignItems: "center", gap: "8px", marginBottom: "20px" }}>
            <Box
              onClick={handleQuoteClick}
              sx={{
                fontFamily: "Poppins",
                fontSize: "13px",
                fontWeight: "600",
                color: "black",
                cursor: "pointer",
                marginRight: "20px",
                "&:hover": {
                  color: themevals.colors.primary,
                },
              }}
            >
              {quoteNumber}
            </Box>
            <Badge variant={badgeVariant} sx={{ fontSize: "11px" }}>
              {badgeText}
            </Badge>
            {isPriceLocked && priceLockDepositAmount && (
              <Box sx={{ fontFamily: "Poppins", fontSize: "11px", color: themevals.colors.info, fontWeight: 600 }}>
                {formatCurrency(priceLockDepositAmount)} deposit
              </Box>
            )}
          </Flex>

          {/* Project Name */}
          <P
            fontFamily="Poppins"
            fontSize="14px"
            fontWeight="500"
            color="black"
            marginTop="0"
            marginBottom="4px"
          >
            {projectName}
          </P>

          {/* Date */}
          <P
            fontFamily="Poppins"
            fontSize="12px"
            color={themevals.colors.dkgrey}
            marginTop="0"
            marginBottom="0"
          >
            Sent {formatDate(sentDate)}
          </P>
        </Box>

        {/* Right Section: Price and Buttons */}
        <Flex sx={{ flexDirection: "column", alignItems: "flex-end", gap: "12px", marginLeft: "16px" }}>
          {/* Amount */}
          <P
            fontFamily="Poppins"
            fontSize="16px"
            fontWeight="600"
            color="black"
            marginTop="0"
            marginBottom="20px"
          >
            {formatCurrency(amount)}
          </P>

          {/* Action Buttons */}
          {renderActions()}
        </Flex>
      </Flex>
    </Box>
  );
};

export default QuoteRow;
